import type { BILLING_ERROR_CODES } from "@amtana/billing-contract";
import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// What a user reads when a billing action fails (packages/module ADR-001).

const EN = {
  subscriptionFrozenTitle: "Your subscription is frozen",
  subscriptionFrozenDescription: "Unfreeze it before upgrading.",
  noSubscriptionTitle: "You don't have an active subscription",
  noSubscriptionDescription: "Choose a plan to continue.",
  offerUsedTitle: "This offer has already been used",
  offerUsedDescription: "Each offer can be used once.",
  invalidWebhookSignatureTitle: "The payment provider's webhook could not be verified",
  invalidWebhookSignatureDescription: "Nothing changed.",
  unknownCheckoutSessionTitle: "No such checkout session",
  unknownCheckoutSessionDescription: "Start a new checkout and try again.",
  seatsNeedPlanTitle: "Upgrade to add more seats",
  seatsNeedPlanDescription: "The free plan does not sell extra seats.",
  autoRechargePackInvalidTitle: "Choose a listed pack size",
  autoRechargePackInvalidDescription: "Pick one of the credit packs shown.",
  invoiceNotFoundTitle: "No such invoice",
  invoiceNotFoundDescription: "This invoice could not be found.",
  simulateRefusedTitle: "Simulation is not available",
  simulateRefusedDescription: "This is a development and test tool only.",
  paymentProviderUnavailableTitle: "This payment method is not available",
  paymentProviderUnavailableDescription: "Try again later, or choose another way to pay.",
  paymentSettingRefusedTitle: "This payment setting cannot be used in production",
  paymentSettingRefusedDescription: "Use the provider's own live address, key and webhook secret.",
  // D320, the back office's own doors: never reached from a customer's own screen, but the
  // mapping below must still be exhaustive over every code billing can throw.
  stripeCustomerNotFoundTitle: "No such Stripe customer",
  stripeCustomerNotFoundDescription: "It was not found in the current mode.",
  stripeSubscriptionNotFoundTitle: "No such Stripe subscription",
  stripeSubscriptionNotFoundDescription: "It was not found in the current mode.",
  stripeSubscriptionMismatchTitle: "That subscription belongs to a different customer",
  stripeSubscriptionMismatchDescription: "Check the ids and try again.",
  // ADR-023 §13: a remote agent's add-on, its awake hours, and a turn on our model.
  remoteAgentRequiredTitle: "Remote agents come with the Remote agent add-on",
  remoteAgentRequiredDescription: "Get it in the store: one machine and its awake hours every month.",
  awakeHoursUsedTitle: "No awake hours left this month",
  awakeHoursUsedDescription:
    "Your agent is paused and nothing more is charged. Buy an hours pack, or wait for next month.",
  aiCreditsUsedTitle: "No AI credits left",
  aiCreditsUsedDescription: "A task on our model needs AI credits. Buy more to go on.",
  planNotOfferedTitle: "This plan is not available",
  planNotOfferedDescription: "Choose Starter, or reload the page to see the plans on offer.",
  aiCreditsOffTitle: "AI credits are not available",
  aiCreditsOffDescription: "Enrichment and tasks on our model are not offered on this account yet.",
} as const;

const ES: Translation<typeof EN> = {
  subscriptionFrozenTitle: "Tu suscripción está congelada",
  subscriptionFrozenDescription: "Descongélala antes de mejorar tu plan.",
  noSubscriptionTitle: "No tienes una suscripción activa",
  noSubscriptionDescription: "Elige un plan para continuar.",
  offerUsedTitle: "Esta oferta ya se usó",
  offerUsedDescription: "Cada oferta se puede usar una sola vez.",
  invalidWebhookSignatureTitle: "No pudimos verificar el aviso del proveedor de pagos",
  invalidWebhookSignatureDescription: "No cambió nada.",
  unknownCheckoutSessionTitle: "Esa sesión de pago no existe",
  unknownCheckoutSessionDescription: "Inicia un pago nuevo e inténtalo de nuevo.",
  seatsNeedPlanTitle: "Mejora tu plan para agregar más puestos",
  seatsNeedPlanDescription: "El plan gratuito no vende puestos extra.",
  autoRechargePackInvalidTitle: "Elige un tamaño de paquete de la lista",
  autoRechargePackInvalidDescription: "Elige uno de los paquetes de créditos que se muestran.",
  invoiceNotFoundTitle: "Esa factura no existe",
  invoiceNotFoundDescription: "No encontramos esta factura.",
  simulateRefusedTitle: "La simulación no está disponible",
  simulateRefusedDescription: "Es una herramienta solo para desarrollo y pruebas.",
  paymentProviderUnavailableTitle: "Este medio de pago no está disponible",
  paymentProviderUnavailableDescription: "Inténtalo más tarde o elige otra forma de pagar.",
  paymentSettingRefusedTitle: "Este ajuste de pagos no se puede usar en producción",
  paymentSettingRefusedDescription:
    "Usa la dirección, la clave y el secreto de webhook reales del proveedor.",
  stripeCustomerNotFoundTitle: "Ese cliente de Stripe no existe",
  stripeCustomerNotFoundDescription: "No se encontró en el modo actual.",
  stripeSubscriptionNotFoundTitle: "Esa suscripción de Stripe no existe",
  stripeSubscriptionNotFoundDescription: "No se encontró en el modo actual.",
  stripeSubscriptionMismatchTitle: "Esa suscripción pertenece a otro cliente",
  stripeSubscriptionMismatchDescription: "Revisa los ids e inténtalo de nuevo.",
  remoteAgentRequiredTitle: "Los agentes remotos vienen con el complemento Agente remoto",
  remoteAgentRequiredDescription: "Consíguelo en la tienda: una máquina y sus horas despierta cada mes.",
  awakeHoursUsedTitle: "No quedan horas despierta este mes",
  awakeHoursUsedDescription:
    "Tu agente está en pausa y no se cobra nada más. Compra un paquete de horas o espera al mes que viene.",
  aiCreditsUsedTitle: "No quedan créditos de IA",
  aiCreditsUsedDescription: "Una tarea con nuestro modelo necesita créditos de IA. Compra más para seguir.",
  planNotOfferedTitle: "Este plan no está disponible",
  planNotOfferedDescription: "Elige Starter, o recarga la página para ver los planes disponibles.",
  aiCreditsOffTitle: "Los créditos de IA no están disponibles",
  aiCreditsOffDescription:
    "El enriquecimiento y las tareas con nuestro modelo aún no se ofrecen en esta cuenta.",
};

export const BILLING_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const BILLING_ERROR_COPY = {
  billing_subscription_frozen: {
    title: phrase(BILLING_ERROR_CATALOGS, "subscriptionFrozenTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "subscriptionFrozenDescription"),
  },
  billing_no_subscription: {
    title: phrase(BILLING_ERROR_CATALOGS, "noSubscriptionTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "noSubscriptionDescription"),
  },
  billing_offer_used: {
    title: phrase(BILLING_ERROR_CATALOGS, "offerUsedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "offerUsedDescription"),
  },
  billing_invalid_webhook_signature: {
    title: phrase(BILLING_ERROR_CATALOGS, "invalidWebhookSignatureTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "invalidWebhookSignatureDescription"),
  },
  billing_unknown_checkout_session: {
    title: phrase(BILLING_ERROR_CATALOGS, "unknownCheckoutSessionTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "unknownCheckoutSessionDescription"),
  },
  billing_seats_need_plan: {
    title: phrase(BILLING_ERROR_CATALOGS, "seatsNeedPlanTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "seatsNeedPlanDescription"),
  },
  billing_auto_recharge_pack_invalid: {
    title: phrase(BILLING_ERROR_CATALOGS, "autoRechargePackInvalidTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "autoRechargePackInvalidDescription"),
  },
  billing_invoice_not_found: {
    title: phrase(BILLING_ERROR_CATALOGS, "invoiceNotFoundTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "invoiceNotFoundDescription"),
  },
  billing_simulate_refused_in_production: {
    title: phrase(BILLING_ERROR_CATALOGS, "simulateRefusedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "simulateRefusedDescription"),
  },
  payment_provider_unavailable: {
    title: phrase(BILLING_ERROR_CATALOGS, "paymentProviderUnavailableTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "paymentProviderUnavailableDescription"),
  },
  payment_setting_refused: {
    title: phrase(BILLING_ERROR_CATALOGS, "paymentSettingRefusedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "paymentSettingRefusedDescription"),
  },
  billing_stripe_customer_not_found: {
    title: phrase(BILLING_ERROR_CATALOGS, "stripeCustomerNotFoundTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "stripeCustomerNotFoundDescription"),
  },
  billing_stripe_subscription_not_found: {
    title: phrase(BILLING_ERROR_CATALOGS, "stripeSubscriptionNotFoundTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "stripeSubscriptionNotFoundDescription"),
  },
  billing_stripe_subscription_mismatch: {
    title: phrase(BILLING_ERROR_CATALOGS, "stripeSubscriptionMismatchTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "stripeSubscriptionMismatchDescription"),
  },
  remote_agent_required: {
    title: phrase(BILLING_ERROR_CATALOGS, "remoteAgentRequiredTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "remoteAgentRequiredDescription"),
  },
  awake_hours_used: {
    title: phrase(BILLING_ERROR_CATALOGS, "awakeHoursUsedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "awakeHoursUsedDescription"),
  },
  ai_credits_used: {
    title: phrase(BILLING_ERROR_CATALOGS, "aiCreditsUsedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "aiCreditsUsedDescription"),
  },
  billing_plan_not_offered: {
    title: phrase(BILLING_ERROR_CATALOGS, "planNotOfferedTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "planNotOfferedDescription"),
  },
  ai_credits_off: {
    title: phrase(BILLING_ERROR_CATALOGS, "aiCreditsOffTitle"),
    description: phrase(BILLING_ERROR_CATALOGS, "aiCreditsOffDescription"),
  },
} satisfies ErrorCopyFor<typeof BILLING_ERROR_CODES>;
